/**
 * Publish-failure amber model — shared by the content card's failures strip
 * and the analytics ChannelTable expansion so both read identically.
 *
 * A failure a retry may still resolve shows AMBER ("retrying"/"paused"), not
 * scary red; red is reserved for the final outcome; green marks a failure a
 * later retry healed. The attempt history always stays visible (this whole
 * feature exists because a failure was once too hard to see) — amber just
 * keeps the transient ones from crying wolf.
 */

export type PublishFailureState = 'retrying' | 'paused' | 'recovered' | 'failed';

/** WHY the last attempt failed — the dispatcher's kind (backend
    tendv2_settings PUBLISH_FAILURE_KINDS). 'rejected' is the one that
    changes the story: the ticket parked on its FIRST attempt because the
    same post would fail the same way, so the copy must say "won't retry"
    rather than let it read as a silent give-up. 'unknown' is a code the
    queue had never met with no reason from the network: one retry of the
    same post, then parked (Snapchat post issue ticket, phase 2). Older rows
    have none. */
export type PublishFailureKind =
  | 'auth' | 'not_linked' | 'account' | 'too_long' | 'rate_limited' | 'transient' | 'unknown' | 'rejected';

export interface PublishFailureLike {
  error_message: string | null;
  platforms: string[];
  state?: PublishFailureState;
  attempts?: number | null;
  max_attempts?: number | null;
  reason?: string | null;
  kind?: PublishFailureKind | string | null;
  recovered_at?: string | null;
  /** HOW the later success happened: 'queue' (a retry) or 'manual' (a
      person's Publish now). A manual publish is not a retry — item 4888
      read "retried & published" for a pin Kinny posted by hand. */
  recovered_via?: 'manual' | 'queue' | string | null;
  /** Set by the caller on the newest row of a content item's history: only
      that row earns the "failed after N attempts" tail. */
  is_final_attempt?: boolean;
  /** The network's numeric error code, when the reply carried one. */
  code?: number | string | null;
  /** The network's own details string, when it sent one (already scrubbed
      of vendor names by the backend). */
  details?: string | null;
}

/** What a bare code means when the network itself said nothing more. Our
    copy, so it never names a vendor. Threads 379 is the first entry (item
    5227, 2026-09-29): the reply was "Error posting to Threads." and nothing
    else, and the same post landed on the next try — the queue now retries
    it (amber) instead of parking. Snapchat 339 (item 5946, 2026-10-29) is
    the same shape: "cannot process your post at this time", nothing else. */
export const PUBLISH_CODE_NOTES: Record<string, string> = {
  '379': 'Threads gave no reason — usually a passing Threads-side error that clears on the next try',
  '339': 'Snapchat gave no reason — usually a passing Snapchat-side processing error that clears on the next try',
};

/** " (code 379)" when the row carries the network's code; '' otherwise. */
export const publishFailureCodeTag = (f: Pick<PublishFailureLike, 'code'>): string =>
  f.code != null && String(f.code).trim() !== '' ? ` (code ${f.code})` : '';

/** The line under a failure's message: the network's own details when it
    sent any (and they say more than the message), else our note for a
    known bare code, else null. */
export const publishFailureDetailLine = (
  f: Pick<PublishFailureLike, 'code' | 'details' | 'error_message'>,
): string | null => {
  const details = (f.details || '').trim();
  if (details && details.toLowerCase() !== (f.error_message || '').trim().toLowerCase()) return details;
  if (f.code != null) return PUBLISH_CODE_NOTES[String(f.code)] || null;
  return null;
};

export const PUBLISH_FAILURE_STYLE: Record<PublishFailureState, { color: string; heading: string }> = {
  retrying:  { color: '#fbbf24', heading: 'Retrying' },
  paused:    { color: '#fbbf24', heading: 'Needs attention' },
  recovered: { color: '#34d399', heading: 'Recovered' },
  failed:    { color: '#f87171', heading: 'Publish failures' },
};

// Most-severe state present drives a section's heading/tone.
export const PUBLISH_FAILURE_SEVERITY: PublishFailureState[] = ['failed', 'paused', 'retrying', 'recovered'];

export const publishFailureHeadState = (rows: Array<{ state?: PublishFailureState }>): PublishFailureState =>
  PUBLISH_FAILURE_SEVERITY.find((s) => rows.some((r) => (r.state || 'failed') === s)) || 'failed';

const fmtWhen = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

/** Colour + trailing status clause + whether to mute the raw error text. */
export const describePublishFailure = (
  f: PublishFailureLike,
): { state: PublishFailureState; color: string; suffix: string; muteError: boolean } => {
  const state: PublishFailureState = f.state || 'failed';
  const color = PUBLISH_FAILURE_STYLE[state].color;
  const max = f.max_attempts ?? 3;
  const attempt = f.attempts ?? null;
  const left = attempt != null ? Math.max(max - attempt, 0) : null;

  if (state === 'retrying') {
    const parts = [`attempt ${attempt ?? '?'} of ${max}`];
    if (left != null) parts.push(`${left} left`);
    // A rate limit waits out a backoff rather than taking the next slot.
    const why = f.kind === 'rate_limited' ? 'rate limited, retrying after a pause'
      : f.kind === 'unknown' ? 'no reason given, retrying once with the same post'
      : 'retrying';
    return { state, color, suffix: ` · ${why} — ${parts.join(', ')}`, muteError: false };
  }
  if (state === 'paused') {
    if (f.reason === 'over_limit') {
      // A SYSTEM hold: nothing was sent; the error text IS the reason
      // ("Post is 416/300 characters for bluesky — edit to resume"), so
      // the suffix only names the mechanism.
      return { state, color, suffix: ' · held — edit under the limit and it resumes on its own', muteError: false };
    }
    if (f.reason === 'not_connected' && f.kind === 'account') {
      // The breaker tripped on an ACCOUNT problem (restriction, identity
      // verification, permissions) — relinking alone may not clear it.
      return { state, color, suffix: ` · held — ${f.platforms.join(', ')} needs attention on the account itself`, muteError: false };
    }
    const why = f.reason === 'held'
      ? 'held — resume to retry'
      : `auto-publish is off for ${f.platforms.join(', ')} — turn it on or publish now`;
    return { state, color, suffix: ` · paused, ${why}`, muteError: false };
  }
  if (state === 'recovered') {
    const when = f.recovered_at ? ` ${fmtWhen(f.recovered_at)}` : '';
    const how = f.recovered_via === 'manual' ? 'published later via Publish now' : 'retried & published';
    return { state, color, suffix: ` · ${how}${when}`, muteError: true };
  }
  // "failed after N attempts" belongs to the attempt that exhausted the
  // ticket, not to every row of its history — `attempt` is ticket-level, so
  // the caller marks the row that was last (isFinalAttempt); rows without
  // that flag keep the legacy behavior.
  const isLast = f.is_final_attempt ?? true;
  if (isLast && f.kind === 'rejected') {
    // Parked on purpose, on the first attempt: the network said no to THIS
    // post and would again. Nothing is coming — say so.
    const who = f.platforms.length ? f.platforms.join(', ') : 'the network';
    return { state, color, suffix: ` · rejected by ${who} — won't retry; fix it and publish now`, muteError: false };
  }
  if (isLast && f.kind === 'unknown') {
    // A code we had never met, no reason given: the queue tried the same
    // post once more and got the same answer. Nothing more is coming.
    const who = f.platforms.length ? f.platforms.join(', ') : 'the network';
    return { state, color, suffix: ` · ${who} said no twice without a reason — publish now to try again`, muteError: false };
  }
  const tail = isLast && attempt != null && attempt >= max ? ` · failed after ${max} attempts` : '';
  return { state, color, suffix: tail, muteError: false };
};
